import React from "react";
import PortfolioPageShell from "../components/portfolio/PortfolioPageShell";

const HIGHLIGHTS = [
  { label: "Institution", value: "IIT Delhi", shadow: "5px 5px 0 0 #4ADE80", accent: "#15803D" },
  { label: "Core Focus", value: "ML + Systems + Optimisation", shadow: "5px 5px 0 0 #F472B6", accent: "#BE185D" },
  { label: "Style", value: "Research to Production", shadow: "5px 5px 0 0 #FBBF24", accent: "#B45309" },
];

const EDUCATION = [
  {
    years: "2019 — 2023",
    degree: "BTech, Mechanical Engineering",
    school: "Indian Institute of Technology Delhi",
    blurb:
      "Mathematical foundations across machine learning, deep learning, linear algebra, probability, operations research, numerical methods, and stochastic processes. Built the optimisation + systems-thinking base I lean on for distributed inference scheduling, capacity planning, and agent design.",
  },
];

const COURSEWORK = [
  "Machine Learning",
  "Deep Learning",
  "Linear Algebra and Differential Equations",
  "Calculus",
  "Operation Research",
  "Probability and Statistics",
  "Numerical Methods and Computations",
  "Stochastic Processes",
];

const SKILL_CATEGORIES = [
  {
    title: "Programming Languages",
    blurb:
      "Day-to-day languages I write production code in.",
    skills: ["Python", "Bash", "JavaScript", "React JS", "Dart", "Flutter"],
    shadow: "5px 5px 0 0 #0369A1",
    accent: "#0369A1",
  },
  {
    title: "LLM Inference & Serving",
    blurb:
      "Frameworks, kernels, and tricks for serving LLMs under real production SLOs.",
    skills: [
      "vLLM",
      "TensorRT-LLM",
      "SGLang",
      "Triton Inference Server",
      "DeepSpeed-Inference",
      "Paged Attention",
      "Continuous Batching",
      "Speculative Decoding",
      "Prefix / KV Cache",
      "FlashAttention",
      "FP8",
      "AWQ",
      "GPTQ",
      "GGUF",
      "Ollama",
    ],
    shadow: "5px 5px 0 0 #15803D",
    accent: "#15803D",
  },
  {
    title: "Model Training & Post-Training",
    blurb:
      "Multi-GPU training, fine-tuning, and alignment pipelines.",
    skills: [
      "PyTorch",
      "DDP",
      "FSDP",
      "DeepSpeed ZeRO-3",
      "Megatron-LM",
      "Mixed Precision",
      "Gradient Checkpointing",
      "LoRA",
      "QLoRA",
      "RLHF",
      "DPO",
      "GRPO",
      "Distillation",
      "HuggingFace",
      "TRL",
      "Accelerate",
    ],
    shadow: "5px 5px 0 0 #A78BFA",
    accent: "#7E22CE",
  },
  {
    title: "Agentic AI & LLM Frameworks",
    blurb:
      "Orchestration, tool calling, retrieval, and the runtime around the model.",
    skills: [
      "Agentic AI",
      "Multi-Agent Orchestration",
      "LangGraph",
      "LangChain",
      "LlamaIndex",
      "MCP",
      "Tool Calling",
      "Advanced RAG",
      "Hybrid Retrieval",
      "GraphRAG",
      "ColBERT",
      "Context Engineering",
      "Pydantic",
      "LangSmith",
    ],
    shadow: "5px 5px 0 0 #F472B6",
    accent: "#BE185D",
  },
  {
    title: "Distributed Systems & Backend",
    blurb:
      "The plumbing under the agent: event-driven backends, RPC, and service infra.",
    skills: [
      "FastAPI",
      "gRPC",
      "Protobuf",
      "Async Python",
      "Kafka",
      "Redis Streams",
      "RabbitMQ",
      "Saga / Idempotency",
      "Event-Driven Architecture",
      "Microservices",
      "REST",
      "WebSockets",
      "Circuit Breakers",
      "Rate Limiting",
    ],
    shadow: "5px 5px 0 0 #FB923C",
    accent: "#B45309",
  },
  {
    title: "Data & Storage",
    blurb:
      "Vector, relational, key-value, and analytical stores for AI workloads.",
    skills: [
      "SQL",
      "PostgreSQL",
      "MongoDB",
      "Redis",
      "Milvus",
      "Qdrant",
      "Pinecone",
      "Chroma",
      "SQLite FTS5",
      "ClickHouse",
      "BigQuery",
      "Snowflake",
      "PySpark",
      "Parquet",
      "Kafka Streams",
    ],
    shadow: "5px 5px 0 0 #BE185D",
    accent: "#BE185D",
  },
  {
    title: "Cloud & Infrastructure",
    blurb:
      "AWS services, containers, and CI/CD I use to ship production AI services.",
    skills: [
      "Git",
      "GitHub",
      "GitHub Actions",
      "GitLab",
      "GitLab CI",
      "Docker",
      "Kubernetes",
      "AWS S3",
      "AWS EC2",
      "AWS DynamoDB",
      "AWS CloudFront",
      "AWS SageMaker",
      "AWS Lambda",
      "AWS ECS",
      "AWS IAM",
      "AWS CloudWatch",
    ],
    shadow: "5px 5px 0 0 #7DD3FC",
    accent: "#0369A1",
  },
  {
    title: "Eval, Guardrails & MLOps",
    blurb:
      "How model quality is measured, gated, and watched in production.",
    skills: [
      "lm-eval-harness",
      "HELM",
      "Paired Bootstrap / MDE",
      "Red Teaming",
      "Presidio",
      "PII / Toxicity Filters",
      "Prompt Injection Defence",
      "MLflow",
      "Weights & Biases",
    ],
    shadow: "5px 5px 0 0 #FBBF24",
    accent: "#B45309",
  },
  {
    title: "Observability & Performance",
    blurb:
      "Tracing, profiling, SLOs, and the discipline to debug a slow request.",
    skills: [
      "OpenTelemetry",
      "Prometheus",
      "Grafana",
      "Datadog",
      "Loki",
      "Jaeger",
      "NVIDIA Nsight",
      "PyTorch Profiler",
      "p50 / p95 / p99 SLOs",
      "Load Testing (k6, Locust)",
      "Capacity Planning",
    ],
    shadow: "5px 5px 0 0 #4ADE80",
    accent: "#15803D",
  },
  {
    title: "Fundamentals & System Design",
    blurb:
      "Core CS and ML fundamentals I lean on for designing scalable AI systems.",
    skills: [
      "Distributed System Design",
      "ML System Design",
      "Algorithms & Data Structures",
      "Concurrency",
      "Networking",
      "Linear Algebra",
      "Probability & Statistics",
      "Operations Research",
      "Optimisation",
    ],
    shadow: "5px 5px 0 0 #A78BFA",
    accent: "#7E22CE",
  },
];

export default function PortfolioEducation() {
  return (
    <PortfolioPageShell
      tag="EDUCATION & SKILLS"
      title="FOUNDATIONS & STACK"
      subtitle="Academic Foundation + Production Stack"
      description="Mechanical Engineering at IIT Delhi, with electives across ML, deep learning, and operations research. The skill stack below is what I actually use to design, ship, and operate scalable AI systems."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
        {HIGHLIGHTS.map((h) => (
          <article
            key={h.label}
            className="bg-white border-[3px] border-[#0A0A0A] p-5"
            style={{ boxShadow: h.shadow }}
          >
            <p
              className="font-arcade text-xs uppercase tracking-widest mb-2"
              style={{ color: h.accent }}
            >
              {h.label}
            </p>
            <h3 className="font-pixel text-sm md:text-base text-[#0A0A0A] leading-snug">
              {h.value}
            </h3>
          </article>
        ))}
      </div>

      <div className="space-y-6 mb-10">
        {EDUCATION.map((edu) => (
          <article
            key={edu.school}
            className="bg-white border-[3px] border-[#0A0A0A] p-6 md:p-8 grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4 md:gap-8"
            style={{ boxShadow: "5px 5px 0 0 #0369A1" }}
          >
            <div className="font-terminal text-sm text-[#0A0A0A]/70">
              <p className="font-arcade text-base text-[#0369A1] mb-1">{edu.years}</p>
              <p>New Delhi, India</p>
            </div>
            <div>
              <h3 className="font-pixel text-sm md:text-base text-[#0A0A0A] leading-snug mb-2">
                {edu.degree}
              </h3>
              <h4 className="font-arcade text-base text-[#0A0A0A]/70 uppercase tracking-wider mb-3">
                {edu.school}
              </h4>
              <p className="font-terminal text-sm md:text-base text-[#0A0A0A]/80 leading-relaxed mb-5">
                {edu.blurb}
              </p>
              <p className="font-arcade text-xs text-[#0369A1] uppercase tracking-widest mb-3">
                &gt; Relevant coursework
              </p>
              <div className="flex flex-wrap gap-2">
                {COURSEWORK.map((c) => (
                  <span
                    key={c}
                    className="font-terminal text-xs text-[#0A0A0A] border border-[#0A0A0A] bg-[#F5F5F0] px-2 py-1"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mb-6">
        <p className="font-arcade text-xs text-[#15803D] uppercase tracking-widest mb-2">
          &gt; Skills.stack
        </p>
        <h2 className="font-pixel text-base md:text-xl text-[#0A0A0A] leading-snug">
          The Stack I Ship With
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {SKILL_CATEGORIES.map((cat) => (
          <article
            key={cat.title}
            className="bg-white border-[3px] border-[#0A0A0A] p-6"
            style={{ boxShadow: cat.shadow }}
          >
            <h3
              className="font-pixel text-sm md:text-base leading-snug mb-2"
              style={{ color: cat.accent }}
            >
              {cat.title}
            </h3>
            <p className="font-terminal text-xs md:text-sm text-[#0A0A0A]/65 leading-relaxed mb-4">
              {cat.blurb}
            </p>
            <div className="flex flex-wrap gap-2">
              {cat.skills.map((s) => (
                <span
                  key={s}
                  className="font-terminal text-xs text-[#0A0A0A] border border-[#0A0A0A] bg-[#F5F5F0] px-2 py-1"
                >
                  {s}
                </span>
              ))}
            </div>
          </article>
        ))}
      </div>
    </PortfolioPageShell>
  );
}
